import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, Validators } from '@angular/forms';
import { Subject, from, of } from 'rxjs';
import { catchError, concatMap, debounceTime, switchMap, takeUntil, tap, toArray } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { Roles } from 'src/app/shared/enums/Roles.enum';
import { MoneyValidator } from 'src/app/shared/validators/money.validator';
import { MinTcemValue } from 'src/app/shared/validators/min-tcem-value.validators';
import { MaxTcemValue } from 'src/app/shared/validators/max-tcem-value.validators';
import { environment } from 'src/environments/environment';
import { InvoiceService } from '../../../services/invoice.service';
import { DEFAULT_CONFIGURATION_TEA } from '../configure-invoice-dialog/configure-invoice-dialog.component';

export const MINIMUM_BACKUP_DAYS = 15;

export const NO_PREVIOUS_VALUE = '---';

type Step = 'edit' | 'review' | 'success';

@Component({
  selector: 'app-configure-conditions-dialog',
  templateUrl: './configure-conditions-dialog.component.html',
  styleUrls: ['./configure-conditions-dialog.component.scss'],
})
export class ConfigureConditionsDialogComponent implements OnInit, OnDestroy {
  form;
  invoice;
  previousConfiguration;
  role;

  step: Step = 'edit';

  originCost;
  originCalculating = false;

  operations = [];

  submitting = false;
  submitError = '';

  private calculationGeneration = 0;

  private conditionsChanged = new Subject<void>();
  private ngUnsubscribe = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ConfigureConditionsDialogComponent>,
    private formBuilder: FormBuilder,
    private service: InvoiceService,
    private spinner: NgxSpinnerService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.invoice = this.data?.invoice;
    this.previousConfiguration = this.data?.previousConfiguration;
    this.role = this.data?.role;

    this.form = this.formBuilder.group({
      tdmPercentage: [
        '',
        this.role === Roles.ACCOUNT_MANGER
          ? [Validators.required, MinTcemValue(1.4), MaxTcemValue(10), MoneyValidator(1, 2)]
          : [Validators.required, MaxTcemValue(10), MoneyValidator(1, 2)],
      ],
      reservationPercentage: ['', [Validators.required, MoneyValidator(1, 2)]],
    });

    this.operations = (this.data?.relatedOperations || [])
      .filter(operation => operation?._id !== this.invoice?._id)
      .map(operation => ({
        ...operation,
        selected: false,
        calculating: false,
        blocked: false,
        cost: null,
      }));

    this.conditionsChanged
      .pipe(debounceTime(600), takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.calculateOrigin());

    this.form.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.resetCalculations();

      if (this.form.valid) {
        this.conditionsChanged.next();
      }
    });

    if (this.form.valid) {
      this.calculateOrigin();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.spinner.hide();
  }

  get conditions() {
    const tdmPercentage = this.form?.value?.tdmPercentage;

    return {
      tdmPercentage,
      reservationPercentage: this.form?.value?.reservationPercentage,
      distributionPercentage: this.service.getDistribution(DEFAULT_CONFIGURATION_TEA, tdmPercentage),
    };
  }

  private firstUsableValue(...values): string {
    const usable = values.find(
      value => value !== null && value !== undefined && value !== '' && value !== NO_PREVIOUS_VALUE
    );

    return usable === undefined ? '' : String(usable);
  }

  get previousTdm(): string {
    return this.firstUsableValue(
      this.previousConfiguration?.tdmPercentage,
      this.data?.tdm,
      this.invoice?.tdmPercentage
    );
  }

  get previousReservation(): string {
    return this.firstUsableValue(
      this.previousConfiguration?.reservationPercentage,
      this.data?.reservation,
      this.invoice?.reservationPercentage
    );
  }

  private resetCalculations() {
    this.calculationGeneration++;
    this.originCost = null;
    this.originCalculating = this.form.valid;
    this.operations.forEach(operation => {
      operation.selected = false;
      operation.calculating = false;
      operation.cost = null;
      operation.blocked = false;
    });
  }

  private calculateOrigin() {
    if (!this.form.valid) return;

    const generation = this.calculationGeneration;
    this.originCalculating = true;

    this.service
      .getCostValues(this.invoice?._id, this.conditions)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(cost => {
        if (generation !== this.calculationGeneration) return;

        this.originCalculating = false;
        this.originCost = cost;
      });
  }

  private calculateOperation(operation) {
    const generation = this.calculationGeneration;
    operation.calculating = true;
    operation.blocked = false;

    this.service
      .getCostValues(operation._id, this.conditions)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(cost => {
        if (generation !== this.calculationGeneration) return;

        operation.calculating = false;
        operation.cost = cost;

        if (this.isBlocked(operation, this.backupDays(operation))) {
          operation.blocked = true;
          operation.selected = false;
        }
      });
  }

  setSelection(operation) {
    if (operation.blocked || operation.calculating || !this.form.valid) return;

    operation.selected = !operation.selected;

    if (operation.selected) {
      this.calculateOperation(operation);
    } else {
      operation.cost = null;
    }
  }

  get selectedOperations() {
    return this.operations.filter(operation => operation.selected);
  }

  get calculatingOperations() {
    return this.operations.filter(operation => operation.calculating);
  }

  get isCalculating(): boolean {
    return this.originCalculating || this.calculatingOperations.length > 0;
  }

  backupDays(operation) {
    return operation?.cost?.evaluationDays;
  }

  get originBackupDays() {
    return this.originCost?.evaluationDays;
  }

  isBlocked(operation, evaluation): boolean {
    if (operation?.isConfirming || !this.isNumber(evaluation)) return false;

    if (Number(evaluation) < 0) return true;

    return this.role === Roles.ACCOUNT_MANGER && Number(evaluation) <= MINIMUM_BACKUP_DAYS;
  }

  isNegativeBackup(evaluation): boolean {
    return this.isNumber(evaluation) && Number(evaluation) < 0;
  }

  blockLimitText(evaluation): string {
    return this.isNegativeBackup(evaluation) ? 'no puede ser negativo' : `el mínimo es ${MINIMUM_BACKUP_DAYS}`;
  }

  get originIsBlocked(): boolean {
    return this.isBlocked(this.invoice, this.originBackupDays);
  }

  get batchSize(): number {
    return 1 + this.selectedOperations.length;
  }

  get canContinue(): boolean {
    return (
      this.form?.valid && !this.isCalculating && !!this.originCost && !this.originIsBlocked && !this.submitting
    );
  }

  get providerName(): string {
    return this.invoice?.isConfirming ? this.invoice?.debtor?.companyName : this.invoice?.issuer?.companyName;
  }

  get providerLabel(): string {
    return this.invoice?.isConfirming ? 'Pagador' : 'Proveedor';
  }

  counterpartyName(operation): string {
    return operation?.isConfirming ? operation?.supplier?.companyName : operation?.debtor?.companyName;
  }

  get operationType(): string {
    return this.invoice?.isConfirming ? 'Confirming' : 'Factoring';
  }

  getDuration(operation) {
    return operation?.actualDuration || operation?.minimumDuration;
  }

  getCodes(operation) {
    return (operation?.physicalInvoices || []).map(i => i.code).join(', ');
  }

  currencySymbol(operation): string {
    return operation?.currencySymbol || (operation?.currency === 'pen' ? 'S/ ' : '$ ');
  }

  amount(operation, value): string {
    return this.currencySymbol(operation) + (value ?? '');
  }

  getEvaluationClass(evaluation): Record<string, boolean> {
    return {
      'eval-red': evaluation <= MINIMUM_BACKUP_DAYS,
      'eval-yellow': evaluation > MINIMUM_BACKUP_DAYS && evaluation <= 30,
      'eval-green': evaluation > 30,
    };
  }

  /** The green tick only goes next to a colchón that is comfortably above the minimum. */
  isHealthyEvaluation(evaluation): boolean {
    return this.isNumber(evaluation) && Number(evaluation) > 30;
  }

  isNumber(value: any) {
    return typeof value === 'number';
  }

  batch = [];

  continueToReview() {
    if (!this.canContinue) return;

    this.batch = [{ ...this.invoice, cost: this.originCost, isOrigin: true }, ...this.selectedOperations];
    this.submitError = '';
    this.step = 'review';
  }

  backToEdit() {
    this.step = 'edit';
  }

  confirm() {
    if (this.submitting) return;

    this.submitting = true;
    this.submitError = '';
    this.spinner.show();

    const invoiceIds = this.batch.map(operation => operation._id);
    const isBulk = invoiceIds.length > 1;
    const payload = {
      ...this.conditions,
      homeUrl: environment.clientUrl,
      ...(isBulk ? { isConditionsBulk: true } : {}),
    };

    let configuredCount = 0;

    from(invoiceIds)
      .pipe(
        concatMap(invoiceId =>
          this.service.update({ ...payload, _id: invoiceId }).pipe(tap(() => configuredCount++))
        ),
        toArray(),
        switchMap(() =>
          isBulk
            ? this.service.sendConditionsBulkEmail(invoiceIds).pipe(catchError(() => of(null)))
            : of(null)
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: () => {
          this.submitting = false;
          this.spinner.hide();
          this.step = 'success';
        },
        error: error => {
          this.submitting = false;
          this.spinner.hide();
          this.submitError = this.failureMessage(error, configuredCount, invoiceIds.length);
        },
      });
  }

  private failureMessage(error, configuredCount, total): string {
    const detail = error?.error?.message || error?.message || '';
    const progress =
      configuredCount > 0
        ? ` Se configuraron ${configuredCount} de ${total} operaciones; las demás siguen en aprobada.`
        : '';

    return `No se pudo completar la configuración.${progress} ${detail}`.trim();
  }

  close() {
    this.dialogRef.close();
  }

  finish() {
    this.dialogRef.close({ configured: true, count: this.batch.length });
  }
}
